// src/pages/ProjectOracle.jsx
// Route: /project-oracle
// Axiom Intelligence — Owner's Project Flow
// Layer 1: Portfolio view (all sites, phase health)
// Layer 2: Site view (IDEF0 A1-A5 phase flow, live CORR-004 colors)
// Layer 3: Activity drilldown (Phase 2)

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactFlow, {
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useNavigate } from 'react-router-dom';
import PhaseOracle, { PHASE_CHILDREN } from './PhaseOracle';

// ─────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────

const API_BASE = process.env.REACT_APP_API_URL || '';

const SITES = [
  { code: 'DALLAS',      name: 'Dallas',       city: 'Dallas / Plano, TX',        state: 'TX', iso: 'ERCOT', region: 'US'    },
  { code: 'ASHBURN',     name: 'Ashburn',      city: 'Ashburn / Sterling, VA',    state: 'VA', iso: 'PJM',   region: 'US'    },
  { code: 'PHOENIX',     name: 'Phoenix',      city: 'Phoenix, AZ',               state: 'AZ', iso: 'WECC',  region: 'US'    },
  { code: 'CHICAGO',     name: 'Chicago',      city: 'Northlake, IL',             state: 'IL', iso: 'MISO',  region: 'US'    },
  { code: 'SALTLAKE',    name: 'Salt Lake',    city: 'West Jordan, UT',           state: 'UT', iso: 'WECC',  region: 'US'    },
  { code: 'FREDERICK',   name: 'Frederick',    city: 'Frederick, MD',             state: 'MD', iso: 'PJM',   region: 'US'    },
  { code: 'PORTLAND',    name: 'Portland',     city: 'Portland, OR',              state: 'OR', iso: 'WECC',  region: 'US'    },
  { code: 'SAOPAULO',    name: 'Sao Paulo',    city: 'Sao Paulo, Brazil',         state: null, iso: null,    region: 'LATAM' },
  { code: 'BOGOTA',      name: 'Bogota',       city: 'Bogota, Colombia',          state: null, iso: null,    region: 'LATAM' },
  { code: 'QUERETARO',   name: 'Queretaro',    city: 'Queretaro, Mexico',         state: null, iso: null,    region: 'LATAM' },
  { code: 'SANTIAGO',    name: 'Santiago',     city: 'Santiago, Chile',           state: null, iso: null,    region: 'LATAM' },
  { code: 'BUENOSAIRES', name: 'Buenos Aires', city: 'Buenos Aires, Argentina',   state: null, iso: null,    region: 'LATAM' },
  { code: 'LIMA',        name: 'Lima',         city: 'Lima, Peru',                state: null, iso: null,    region: 'LATAM' },
];

const SITE_GEO = {
  PORTLAND:  { x: 12, y: 25 },
  SALTLAKE:  { x: 24, y: 38 },
  PHOENIX:   { x: 22, y: 58 },
  DALLAS:    { x: 42, y: 68 },
  CHICAGO:   { x: 58, y: 32 },
  FREDERICK: { x: 76, y: 38 },
  ASHBURN:   { x: 78, y: 42 },
};

// US outline as simplified SVG path (percentage-based viewBox)
const US_PATH = "M 8,22 L 12,18 L 18,16 L 26,14 L 35,14 L 45,12 L 55,12 L 65,14 L 72,16 L 78,18 L 84,20 L 88,24 L 90,30 L 88,36 L 85,40 L 82,44 L 80,50 L 78,56 L 74,62 L 70,68 L 64,72 L 58,74 L 50,76 L 42,74 L 36,72 L 30,68 L 26,62 L 22,56 L 18,52 L 14,46 L 10,38 L 8,30 Z";

const SITE_META = {
  FREDERICK:  { iso: 'PJM'   },
  ASHBURN:    { iso: 'PJM'   },
  DALLAS:     { iso: 'ERCOT' },
  CHICAGO:    { iso: 'MISO'  },
  PHOENIX:    { iso: 'WECC'  },
  SALTLAKE:   { iso: 'WECC'  },
  PORTLAND:   { iso: 'WECC'  },
};

// IDEF0 Phase definitions
const PHASES = [
  {
    id: 'A1',
    label: 'Pre-Development',
    sub: 'Month 1-6',
    icon: '[A1]',
    activities: ['Site Evaluation & Selection', 'Project Feasibility & Pro Forma', 'Program Development + Team Assembly'],
    corr004Keys: ['queue_delay', 'site_risk'],
    description: 'Site selection, feasibility, program definition',
  },
  {
    id: 'A2',
    label: 'Design',
    sub: 'Month 3-12',
    icon: '[A2]',
    activities: ['Design Management + Value Engineering', 'Long-Lead Procurement + Coordination'],
    corr004Keys: ['procurement_risk'],
    description: 'Design management and long-lead procurement',
  },
  {
    id: 'A3',
    label: 'Construction',
    sub: 'Month 9-24',
    icon: '[A3]',
    activities: ['Schedule & Cost Management', 'Quality & Vendor Management'],
    corr004Keys: ['queue_delay', 'procurement_risk'],
    description: 'Schedule, cost, quality and vendor management',
  },
  {
    id: 'A4',
    label: 'Commissioning',
    sub: 'Month 18-26',
    icon: '[A4]',
    activities: ['Plan Review + Functional Performance Testing', 'Tenant Acceptance Testing'],
    corr004Keys: ['site_risk'],
    description: 'Commissioning and tenant acceptance',
  },
  {
    id: 'A5',
    label: 'Turnover',
    sub: 'Month 24+',
    icon: '[A5]',
    activities: ['Close-Out + Operations Readiness', 'Warranty Period Management'],
    corr004Keys: [],
    description: 'Close-out and post-occupancy operations',
  },
];

// ─────────────────────────────────────────────────────────────
// UTILITIES
// ─────────────────────────────────────────────────────────────

const getPhaseScore = (phaseCorr004Keys, siteCorr004) => {
  if (!siteCorr004 || phaseCorr004Keys.length === 0) return null;
  const scores = phaseCorr004Keys
    .map(k => siteCorr004[k]?.score)
    .filter(s => s !== null && s !== undefined);
  if (scores.length === 0) return null;
  return Math.max(...scores);
};

const scoreToColor = (score) => {
  if (score === null || score === undefined) return {
    bg: '#f8fafc', border: '#cbd5e1', text: '#64748b',
    label: 'No Data', dot: '#94a3b8', glow: 'none',
  };
  if (score >= 75) return {
    bg: '#fef2f2', border: '#f87171', text: '#dc2626',
    label: 'High Risk', dot: '#ef4444', glow: '0 0 12px rgba(239,68,68,0.3)',
  };
  if (score >= 50) return {
    bg: '#fffbeb', border: '#fbbf24', text: '#d97706',
    label: 'Moderate', dot: '#f59e0b', glow: '0 0 12px rgba(245,158,11,0.3)',
  };
  if (score >= 25) return {
    bg: '#fefce8', border: '#facc15', text: '#ca8a04',
    label: 'Low-Mod', dot: '#eab308', glow: 'none',
  };
  return {
    bg: '#f0fdf4', border: '#4ade80', text: '#16a34a',
    label: 'Low Risk', dot: '#22c55e', glow: 'none',
  };
};

const getSiteOverallScore = (siteCorr004) => {
  if (!siteCorr004) return null;
  const scores = ['queue_delay', 'site_risk', 'procurement_risk']
    .map(k => siteCorr004[k]?.score)
    .filter(s => s !== null && s !== undefined);
  if (scores.length === 0) return null;
  return Math.max(...scores);
};

// ─────────────────────────────────────────────────────────────
// CUSTOM REACT FLOW NODES
// ─────────────────────────────────────────────────────────────

const A0Node = ({ data }) => (
  <div style={{
    background: 'linear-gradient(135deg, #1e3a8a, #1e40af)',
    border: '2px solid #3b82f6',
    borderRadius: 16,
    padding: '16px 24px',
    minWidth: 280,
    textAlign: 'center',
    boxShadow: '0 8px 32px rgba(30,58,138,0.4)',
    cursor: 'pointer',
  }}>
    <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
    <div style={{ color: '#93c5fd', fontSize: 10, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: 2, marginBottom: 4 }}>
      A0 CONTEXT
    </div>
    <div style={{ color: '#fff', fontSize: 15, fontWeight: 800, lineHeight: 1.3 }}>
      {data.label}
    </div>
    <div style={{ color: '#bfdbfe', fontSize: 10, marginTop: 6 }}>
      {data.sub}
    </div>
    {data.siteCount && (
      <div style={{ marginTop: 10, display: 'flex', justifyContent: 'center', gap: 16 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#fff', fontSize: 18, fontWeight: 800 }}>{data.siteCount}</div>
          <div style={{ color: '#93c5fd', fontSize: 9 }}>SITES</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: data.highRiskCount > 0 ? '#fca5a5' : '#86efac',
                        fontSize: 18, fontWeight: 800 }}>{data.highRiskCount}</div>
          <div style={{ color: '#93c5fd', fontSize: 9 }}>HIGH RISK</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#fff', fontSize: 18, fontWeight: 800 }}>{data.usCount}</div>
          <div style={{ color: '#93c5fd', fontSize: 9 }}>US SITES</div>
        </div>
      </div>
    )}
  </div>
);

const PhaseNode = ({ data }) => {
  const colors = scoreToColor(data.score);
  return (
    <div
      onClick={data.onClick}
      style={{
        background: colors.bg,
        border: `2px solid ${colors.border}`,
        borderRadius: 14,
        padding: '14px 18px',
        minWidth: 180,
        cursor: 'pointer',
        boxShadow: colors.glow !== 'none' ? colors.glow : '0 2px 8px rgba(0,0,0,0.08)',
        transition: 'transform 0.15s, box-shadow 0.15s',
      }}
      onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.03)'}
      onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
    >
      <Handle type="target" position={Position.Left}  style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 900,
                       color: '#1e40af', fontFamily: 'monospace' }}>
          {data.id}
        </span>
        <div>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#1e293b' }}>{data.id}</div>
          <div style={{ fontSize: 9, color: '#64748b', textTransform: 'uppercase',
                        letterSpacing: 1 }}>{data.sub}</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%',
                        background: colors.dot,
                        boxShadow: colors.glow !== 'none'
                          ? `0 0 6px ${colors.dot}` : 'none' }} />
        </div>
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>
        {data.label}
      </div>
      <div style={{ fontSize: 10, color: colors.text, fontWeight: 600 }}>
        {data.score !== null ? `${Math.round(data.score)}/100 \u00B7 ${colors.label}` : colors.label}
      </div>
      {data.activities?.slice(0, 2).map((a, i) => (
        <div key={i} style={{ fontSize: 9, color: '#64748b', marginTop: 3,
                              display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 4, height: 4, borderRadius: '50%',
                        background: colors.border, flexShrink: 0 }} />
          {a}
        </div>
      ))}
      <div style={{ marginTop: 8, fontSize: 9, color: colors.text,
                    fontWeight: 600, textAlign: 'right' }}>
        Click to drill down \u2192
      </div>
    </div>
  );
};

const IOCMNode = ({ data }) => (
  <div style={{
    background: data.type === 'control' ? '#f0f9ff'
              : data.type === 'mechanism' ? '#faf5ff'
              : '#f8fafc',
    border: `1px solid ${
      data.type === 'control' ? '#bae6fd'
      : data.type === 'mechanism' ? '#e9d5ff'
      : '#e2e8f0'
    }`,
    borderRadius: 8,
    padding: '6px 12px',
    fontSize: 10,
    color: data.type === 'control' ? '#0369a1'
         : data.type === 'mechanism' ? '#7c3aed'
         : '#475569',
    fontWeight: 600,
    maxWidth: 180,
    textAlign: 'center',
  }}>
    <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
    <Handle type="target" position={Position.Left}  style={{ opacity: 0 }} />
    {data.label}
  </div>
);

const nodeTypes = {
  a0Node:    A0Node,
  phaseNode: PhaseNode,
  iocmNode:  IOCMNode,
};

// ─────────────────────────────────────────────────────────────
// BUILD SITE FLOW (Layer 2 — A1-A5 for one site)
// ─────────────────────────────────────────────────────────────

const buildSiteFlow = (site, siteCorr004, onPhaseClick) => {
  const nodes = [];
  const edges = [];

  nodes.push({
    id: 'a0',
    type: 'a0Node',
    position: { x: 400, y: 20 },
    data: {
      label: `Aligned ${site.name}`,
      sub: `${site.city} \u00B7 ${site.iso || site.region}`,
    },

  });

  // Phase nodes will span x: 180 to 180 + 4*155 = 800
  // Centre of phase row: ~490
  // Controls fan above centre, mechanisms fan below
  const iocmItems = [
    // Inputs — left side, vertically distributed
    { id: 'i1', label: 'Site Data + LBNL Queue + EIA Grid',    type: 'input',     x: 20,  y: 220 },
    { id: 'i2', label: 'Utility Queue + Long-Lead Quotes',     type: 'input',     x: 20,  y: 310 },
    { id: 'i3', label: 'Pro Forma + Tenant Specs',             type: 'input',     x: 20,  y: 400 },
    { id: 'i4', label: 'AISentry Live Telemetry',              type: 'input',     x: 20,  y: 490 },
    // Controls — fanned above, clear of phase nodes
    { id: 'c1', label: '16 Policy Domains + Lender Covenants', type: 'control',   x: 220, y: -60 },
    { id: 'c2', label: 'Hyperscaler SLAs + AHJ Rules',         type: 'control',   x: 600, y: -60 },
    // Outputs — right side, vertically distributed
    { id: 'o1', label: 'Daily Briefing + Risk Oracle',         type: 'output',    x: 1020, y: 220 },
    { id: 'o2', label: 'Schedule + Cost Forecast',             type: 'output',    x: 1020, y: 310 },
    { id: 'o3', label: 'Commissioning Sign-off',               type: 'output',    x: 1020, y: 400 },
    // Mechanisms — fanned below, clear of phase nodes
    { id: 'm2', label: 'Axiom Reasoning Engine + GPT-4.1',    type: 'mechanism', x: 430,  y: 560 },
    { id: 'm3', label: 'AISentry Edge Agents',                 type: 'mechanism', x: 700,  y: 560 },
  ];

  iocmItems.forEach(item => {
    nodes.push({
      id: item.id,
      type: 'iocmNode',
      position: { x: item.x, y: item.y },
      data: { label: item.label, type: item.type },
  
    });
  });

  const phaseStartX = 180;
  const phaseY      = 300;
  const phaseGap    = 155;

  PHASES.forEach((phase, idx) => {
    const score = getPhaseScore(phase.corr004Keys, siteCorr004);
    nodes.push({
      id:       phase.id,
      type:     'phaseNode',
      position: { x: phaseStartX + idx * phaseGap, y: phaseY },
      data: {
        ...phase,
        score,
        onClick: () => onPhaseClick(phase, score, siteCorr004),
      },
  
    });
  });

  for (let i = 0; i < PHASES.length - 1; i++) {
    edges.push({
      id:       `phase-${i}`,
      source:   PHASES[i].id,
      target:   PHASES[i + 1].id,
      animated: false,
      style:    { stroke: '#94a3b8', strokeWidth: 2 },
    });
  }

  ['i1','i2','i3','i4'].forEach(id => {
    edges.push({ id: `${id}-a1`, source: id, target: 'A1',
                 style: { stroke: '#94a3b8', strokeWidth: 1 } });
  });

  ['o1','o2','o3'].forEach(id => {
    edges.push({ id: `a5-${id}`, source: 'A5', target: id,
                 style: { stroke: '#94a3b8', strokeWidth: 1 } });
  });

  return { nodes, edges };
};

// ─────────────────────────────────────────────────────────────
// PORTFOLIO SITE CARD (Layer 1)
// ─────────────────────────────────────────────────────────────

const SiteSelector = ({ sites, selectedSite, onSelect, show, onToggle }) => (
  <div style={{ position: 'relative' }} data-site-selector>
    <button
      onClick={onToggle}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 14px', background: '#fff',
        border: '1px solid #e2e8f0', borderRadius: 10,
        cursor: 'pointer', fontSize: 13, fontWeight: 600,
        color: '#374151', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      }}>
      <span style={{ fontSize: 11, color: '#6b7280' }}>DC SITE</span>
      <span>{selectedSite ? selectedSite.name : 'All Sites'}</span>
      <svg width="12" height="12" fill="none" stroke="currentColor"
           strokeWidth="2" viewBox="0 0 24 24"
           style={{ transform: show ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.2s' }}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/>
      </svg>
    </button>

    {show && (
      <div style={{
        position: 'absolute', top: '100%', left: 0, marginTop: 6,
        background: '#fff', border: '1px solid #e2e8f0',
        borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        zIndex: 100, minWidth: 220, overflow: 'hidden',
      }}>
        <div
          onClick={() => { onSelect(null); onToggle(); }}
          style={{
            padding: '10px 14px', cursor: 'pointer', fontSize: 12,
            fontWeight: 600, color: '#374151',
            background: !selectedSite ? '#f0f9ff' : '#fff',
            borderBottom: '1px solid #f1f5f9',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
          onMouseLeave={e => e.currentTarget.style.background =
            !selectedSite ? '#f0f9ff' : '#fff'}
        >
          All Sites — Portfolio View
        </div>

        <div style={{ padding: '6px 14px 2px',
                      fontSize: 9, fontWeight: 700, color: '#9ca3af',
                      textTransform: 'uppercase', letterSpacing: 1 }}>
          US Portfolio
        </div>
        {sites.filter(s => s.region === 'US').map(site => {
          const isSelected = selectedSite?.code === site.code;
          return (
            <div
              key={site.code}
              onClick={() => { onSelect(site); onToggle(); }}
              style={{
                padding: '9px 14px', cursor: 'pointer', fontSize: 12,
                fontWeight: isSelected ? 700 : 500,
                color: isSelected ? '#1e40af' : '#374151',
                background: isSelected ? '#eff6ff' : '#fff',
                display: 'flex', justifyContent: 'space-between',
                alignItems: 'center',
              }}
              onMouseEnter={e => e.currentTarget.style.background =
                isSelected ? '#eff6ff' : '#f8fafc'}
              onMouseLeave={e => e.currentTarget.style.background =
                isSelected ? '#eff6ff' : '#fff'}
            >
              <span>{site.name}</span>
              <span style={{ fontSize: 10, color: '#9ca3af' }}>
                {site.iso}
              </span>
            </div>
          );
        })}

        <div style={{ padding: '6px 14px 2px', marginTop: 4,
                      fontSize: 9, fontWeight: 700, color: '#9ca3af',
                      textTransform: 'uppercase', letterSpacing: 1,
                      borderTop: '1px solid #f1f5f9' }}>
          LATAM Portfolio
        </div>
        {sites.filter(s => s.region === 'LATAM').map(site => {
          const isSelected = selectedSite?.code === site.code;
          return (
            <div
              key={site.code}
              onClick={() => { onSelect(site); onToggle(); }}
              style={{
                padding: '9px 14px', cursor: 'pointer', fontSize: 12,
                fontWeight: isSelected ? 700 : 500,
                color: isSelected ? '#15803d' : '#374151',
                background: isSelected ? '#f0fdf4' : '#fff',
                display: 'flex', justifyContent: 'space-between',
                alignItems: 'center',
              }}
              onMouseEnter={e => e.currentTarget.style.background =
                isSelected ? '#f0fdf4' : '#f8fafc'}
              onMouseLeave={e => e.currentTarget.style.background =
                isSelected ? '#f0fdf4' : '#fff'}
            >
              <span>{site.name}</span>
              <span style={{ fontSize: 10, color: '#9ca3af' }}>
                {site.region}
              </span>
            </div>
          );
        })}
      </div>
    )}
  </div>
);

const PortfolioCommandCenter = ({ sites, corr004,
                                    onSiteClick }) => {
    const [hoveredSite, setHoveredSite] = useState(null);
    const [selectedSite, setSelectedSite] = useState(null);

    const usSites = sites.filter(s => s.region === 'US').map(s => ({
      ...s,
      queueScore: corr004[s.code]?.queue_delay?.score ?? null,
      siteScore:  corr004[s.code]?.site_risk?.score ?? null,
      procScore:  corr004[s.code]?.procurement_risk?.score ?? null,
      summary:    corr004[s.code]?.queue_delay?.summary ?? null,
      computedAt: corr004[s.code]?.queue_delay?.computed_at ?? null,
    }));

    const latamSites = sites.filter(s => s.region === 'LATAM');

    // Fixed bubble positions — spread across canvas
    const BUBBLE_POS = {
      FREDERICK: { cx: 72, cy: 32 },
      ASHBURN:   { cx: 68, cy: 52 },
      DALLAS:    { cx: 42, cy: 65 },
      CHICAGO:   { cx: 55, cy: 28 },
      PHOENIX:   { cx: 18, cy: 58 },
      SALTLAKE:  { cx: 22, cy: 35 },
      PORTLAND:  { cx: 11, cy: 22 },
    };

    const bubbleRadius = (score) => {
      if (score === null || score === undefined) return 0.9;
      if (score >= 75) return 2;
      if (score >= 50) return 1.6;
      if (score >= 25) return 1.25;
      return 1;
    };

    const riskColor = (score) => {
      if (score === null || score === undefined)
        return { main: '#334155', light: '#475569',
                 dark: '#1e293b', glow: 'none' };
      if (score >= 75)
        return { main: '#dc2626', light: '#f87171',
                 dark: '#7f1d1d', glow: '#ef4444' };
      if (score >= 50)
        return { main: '#d97706', light: '#fbbf24',
                 dark: '#78350f', glow: '#f59e0b' };
      if (score >= 25)
        return { main: '#ca8a04', light: '#fde047',
                 dark: '#713f12', glow: '#eab308' };
      return { main: '#15803d', light: '#4ade80',
               dark: '#14532d', glow: '#22c55e' };
    };

    const feed = [...usSites]
      .sort((a, b) => (b.queueScore ?? 0) - (a.queueScore ?? 0));

    return (
      <div style={{
        background: '#020617',
        height: 'calc(100vh - 52px)',
        display: 'flex', flexDirection: 'column',
      }}>

        {/* ── MAIN CANVAS ──────────────────────────── */}
        <div style={{
          flex: 1, display: 'grid',
          gridTemplateColumns: '1fr 300px',
          minHeight: 0,
        }}>

          {/* SVG bubble canvas */}
          <div style={{ position: 'relative', overflow: 'hidden' }}>
            <svg
              viewBox="0 0 100 90"
              style={{ width: '100%', height: '100%' }}
              preserveAspectRatio="xMidYMid meet"
            >
              <defs>
                {usSites.map(site => {
                  const pos = BUBBLE_POS[site.code];
                  if (!pos) return null;
                  const c = riskColor(site.queueScore);
                  return (
                    <radialGradient
                      key={`grad-${site.code}`}
                      id={`grad-${site.code}`}
                      cx="35%" cy="30%"
                      r="65%"
                      fx="35%" fy="30%"
                    >
                      <stop offset="0%"
                        stopColor={c.light} stopOpacity="0.95" />
                      <stop offset="40%"
                        stopColor={c.main} stopOpacity="1" />
                      <stop offset="100%"
                        stopColor={c.dark} stopOpacity="1" />
                    </radialGradient>
                  );
                })}

                {/* Glow filter */}
                <filter id="glow" x="-50%" y="-50%"
                        width="200%" height="200%">
                  <feGaussianBlur stdDeviation="1.5"
                    result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>

                {/* Strong glow for high risk */}
                <filter id="glow-strong" x="-100%"
                        y="-100%" width="300%" height="300%">
                  <feGaussianBlur stdDeviation="2.5"
                    result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>

              {/* Grid dots — subtle background */}
              {Array.from({ length: 10 }, (_, i) =>
                Array.from({ length: 9 }, (_, j) => (
                  <circle key={`${i}-${j}`}
                    cx={i * 11 + 5} cy={j * 10 + 5}
                    r="0.15" fill="#1e293b" />
                ))
              )}

              {/* PJM corridor connection */}
              {(() => {
                const ash = BUBBLE_POS.ASHBURN;
                const fred = BUBBLE_POS.FREDERICK;
                if (!ash || !fred) return null;
                return (
                  <>
                    <line
                      x1={ash.cx} y1={ash.cy}
                      x2={fred.cx} y2={fred.cy}
                      stroke="#ef4444" strokeWidth="0.3"
                      strokeDasharray="1.5 1"
                      opacity="0.4"
                    />
                    <text
                      x={(ash.cx + fred.cx) / 2 + 2}
                      y={(ash.cy + fred.cy) / 2 - 1.5}
                      fill="#ef4444" fontSize="2.2"
                      opacity="0.6" textAnchor="middle">
                      PJM corridor
                    </text>
                  </>
                );
              })()}

              {/* Site bubbles */}
              {usSites.map(site => {
                const pos = BUBBLE_POS[site.code];
                if (!pos) return null;
                const c = riskColor(site.queueScore);
                const r = bubbleRadius(site.queueScore);
                const isHovered = hoveredSite === site.code;
                const isHighRisk = site.queueScore >= 75;
                const isMedRisk = site.queueScore >= 50;

                return (
                  <g
                    key={site.code}
                    onClick={() => onSiteClick(site)}
                    onMouseEnter={() => setHoveredSite(site.code)}
                    onMouseLeave={() => setHoveredSite(null)}
                    style={{ cursor: 'pointer' }}
                  >
                    {/* Outer glow ring — high risk only */}
                    {(isHighRisk || isMedRisk) && (
                      <circle
                        cx={pos.cx} cy={pos.cy}
                        r={r * 2.2}
                        fill={c.glow}
                        opacity={isHighRisk ? 0.08 : 0.05}
                        filter="url(#glow-strong)"
                      />
                    )}

                    {/* Mid glow ring */}
                    {(isHighRisk || isMedRisk) && (
                      <circle
                        cx={pos.cx} cy={pos.cy}
                        r={r * 1.5}
                        fill="none"
                        stroke={c.glow}
                        strokeWidth="0.3"
                        opacity={isHovered ? 0.6 : 0.3}
                      />
                    )}

                    {/* Main sphere */}
                    <circle
                      cx={pos.cx} cy={pos.cy}
                      r={isHovered ? r * 1.08 : r}
                      fill={`url(#grad-${site.code})`}
                      filter={isHighRisk || isMedRisk
                        ? 'url(#glow)' : 'none'}
                      style={{ transition: 'r 0.2s' }}
                    />

                    {/* Specular highlight — top-left gleam */}
                    <ellipse
                      cx={pos.cx - r * 0.28}
                      cy={pos.cy - r * 0.28}
                      rx={r * 0.35}
                      ry={r * 0.22}
                      fill="white"
                      opacity="0.25"
                      transform={`rotate(-35,
                        ${pos.cx - r * 0.28},
                        ${pos.cy - r * 0.28})`}
                    />

                    {/* Score text inside bubble */}
                    {r >= 1.2 && (
                      <text
                        x={pos.cx} y={pos.cy + 0.3}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill="white"
                        fontSize={r >= 1.8 ? "1.2" : "0.9"}
                        fontWeight="700"
                        fontFamily="monospace"
                        opacity="0.95"
                      >
                        {site.queueScore !== null
                          ? Math.round(site.queueScore)
                          : '?'}
                      </text>
                    )}

                    {/* Site name below bubble */}
                    <text
                      x={pos.cx}
                      y={pos.cy + r + 2}
                      textAnchor="middle"
                      fill={isHovered ? '#f1f5f9' : '#64748b'}
                      fontSize="1.8"
                      fontWeight="500"
                      style={{ transition: 'fill 0.15s' }}
                    >
                      {site.name}
                    </text>

                    {/* ISO label */}
                    <text
                      x={pos.cx}
                      y={pos.cy + r + 3.8}
                      textAnchor="middle"
                      fill="#334155"
                      fontSize="1.4"
                    >
                      {SITE_META?.[site.code]?.iso ||
                       site.iso || ''}
                    </text>

                    {/* Hover tooltip */}
                    {isHovered && (
                      <g>
                        <rect
                          x={pos.cx - 18}
                          y={pos.cy - r - 14}
                          width="36" height="11"
                          rx="1.5"
                          fill="#0f172a"
                          stroke="#334155"
                          strokeWidth="0.3"
                        />
                        <text
                          x={pos.cx}
                          y={pos.cy - r - 9}
                          textAnchor="middle"
                          fill="#f1f5f9"
                          fontSize="2.8"
                          fontWeight="600"
                        >
                          {site.name} {'\u00B7'} {
                            site.queueScore !== null
                              ? Math.round(site.queueScore)
                              : '\u2014'}/100
                        </text>
                        <text
                          x={pos.cx}
                          y={pos.cy - r - 5.5}
                          textAnchor="middle"
                          fill="#64748b"
                          fontSize="2.2"
                        >
                          Click to open project flow
                        </text>
                      </g>
                    )}
                  </g>
                );
              })}

              {/* Legend */}
              {[
                { c: '#ef4444', l: 'High risk 75+' },
                { c: '#f59e0b', l: 'Moderate 50\u201374' },
                { c: '#eab308', l: 'Low-mod 25\u201349' },
                { c: '#22c55e', l: 'Low risk <25' },
              ].map((item, i) => (
                <g key={item.l}
                   transform={`translate(2, ${76 + i * 2.8})`}>
                  <circle cx="0.8" cy="0.8" r="0.8"
                    fill={item.c} opacity="0.8" />
                  <text x="2.5" y="1.4" fill="#475569"
                    fontSize="1.6">{item.l}</text>
                </g>
              ))}
            </svg>

            {/* Axiom watermark */}
            <div style={{
              position: 'absolute', bottom: 16, left: 20,
              fontSize: 10, color: '#1e293b', fontWeight: 500,
              letterSpacing: '0.1em', textTransform: 'uppercase',
            }}>
              Axiom {'\u00B7'} CORR-004 {'\u00B7'} Queue delay intelligence
            </div>
          </div>

          {/* ── RIGHT RAIL ─────────────────────────── */}
          <div style={{
            background: '#0a0f1e',
            borderLeft: '0.5px solid #1e293b',
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
          }}>
            {/* Feed header */}
            <div style={{
              padding: '20px 18px 12px',
              borderBottom: '0.5px solid #1e293b',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <div style={{
                width: 6, height: 6, borderRadius: '50%',
                background: '#22c55e',
                boxShadow: '0 0 6px #22c55e',
              }} />
              <span style={{
                fontSize: 10, fontWeight: 500, color: '#38bdf8',
                textTransform: 'uppercase', letterSpacing: '0.12em',
              }}>
                Live Intelligence Feed
              </span>
            </div>

            {/* Site cards */}
            <div style={{
              flex: 1, overflowY: 'auto', padding: '12px 14px',
              display: 'flex', flexDirection: 'column', gap: 8,
            }}>
              {feed.map(site => {
                const c = riskColor(site.queueScore);
                const isHov = hoveredSite === site.code;
                return (
                  <div
                    key={site.code}
                    onClick={() => onSiteClick(site)}
                    onMouseEnter={() => setHoveredSite(site.code)}
                    onMouseLeave={() => setHoveredSite(null)}
                    style={{
                      background: isHov ? '#1e293b' : '#111827',
                      border: `0.5px solid ${
                        isHov ? '#334155' : '#1e293b'
                      }`,
                      borderLeft: `2px solid ${c.main}`,
                      borderRadius: 8,
                      padding: '10px 12px',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center', marginBottom: 4,
                    }}>
                      <span style={{
                        fontSize: 12, fontWeight: 600,
                        color: '#f1f5f9',
                      }}>{site.name}</span>
                      <span style={{
                        fontSize: 14, fontWeight: 700,
                        color: c.main,
                        textShadow: c.glow !== 'none'
                          ? `0 0 8px ${c.glow}` : 'none',
                      }}>
                        {site.queueScore !== null
                          ? Math.round(site.queueScore)
                          : '\u2014'}/100
                      </span>
                    </div>
                    <div style={{
                      display: 'flex', gap: 8, marginBottom: 6,
                    }}>
                      {[
                        { l: 'Site', v: site.siteScore },
                        { l: 'Proc', v: site.procScore },
                      ].map(({ l, v }) => (
                        <div key={l} style={{
                          fontSize: 9, color: '#475569',
                          display: 'flex', gap: 3,
                        }}>
                          <span>{l}:</span>
                          <span style={{ color: '#64748b',
                                         fontWeight: 600 }}>
                            {v !== null ? Math.round(v) : '\u2014'}
                          </span>
                        </div>
                      ))}
                    </div>
                    {site.summary && (
                      <div style={{
                        fontSize: 10, color: '#374151',
                        lineHeight: 1.5,
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}>
                        {site.summary}
                      </div>
                    )}
                    {site.computedAt && (
                      <div style={{
                        fontSize: 9, color: '#1e293b', marginTop: 4,
                      }}>
                        {new Date(site.computedAt)
                          .toLocaleDateString()}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── LATAM STRIP ───────────────────────────── */}
        <div style={{
          background: '#0a0f1e',
          borderTop: '0.5px solid #1e293b',
          padding: '16px 24px',
          display: 'flex', alignItems: 'center', gap: 16,
          flexWrap: 'wrap',
        }}>
          <div style={{
            fontSize: 9, fontWeight: 500, letterSpacing: '0.15em',
            color: '#334155', textTransform: 'uppercase',
            whiteSpace: 'nowrap',
          }}>
            LATAM {'\u00B7'} Outside US coverage
          </div>
          <div style={{ width: '0.5px', height: 16,
                        background: '#1e293b' }} />
          {latamSites.map(site => (
            <div
              key={site.code}
              onClick={() => onSiteClick(site)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                cursor: 'pointer', padding: '5px 10px',
                borderRadius: 6,
                border: '0.5px solid #1e293b',
                background: '#111827',
                transition: 'border-color 0.15s',
              }}
              onMouseEnter={e =>
                e.currentTarget.style.borderColor = '#334155'}
              onMouseLeave={e =>
                e.currentTarget.style.borderColor = '#1e293b'}
            >
              <div style={{
                width: 6, height: 6, borderRadius: '50%',
                background: '#1e3a5f',
                border: '0.5px solid #334155',
              }} />
              <span style={{ fontSize: 11, color: '#475569',
                             fontWeight: 500 }}>
                {site.name}
              </span>
              <span style={{ fontSize: 9, color: '#1e293b' }}>
                {site.city.split(',')[1]?.trim() || ''}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

const PortfolioSiteCard = ({ site, siteCorr004, onClick }) => {
  const overallScore = getSiteOverallScore(siteCorr004);
  const colors       = scoreToColor(overallScore);
  const qd = siteCorr004?.['queue_delay'];
  const sr = siteCorr004?.['site_risk'];
  const pr = siteCorr004?.['procurement_risk'];

  return (
    <div
      onClick={onClick}
      style={{
        background: colors.bg,
        border: `2px solid ${colors.border}`,
        borderRadius: 14,
        padding: '14px 16px',
        cursor: 'pointer',
        boxShadow: colors.glow !== 'none' ? colors.glow : '0 2px 8px rgba(0,0,0,0.06)',
        transition: 'transform 0.15s',
        position: 'relative',
        overflow: 'hidden',
      }}
      onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.02)'}
      onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
    >
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        background: colors.border,
      }} />

      <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'flex-start', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>
            {site.name}
          </div>
          <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>
            {site.city}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {site.region === 'LATAM' && (
            <span style={{ fontSize: 9, padding: '2px 6px', background: '#f0fdf4',
                           border: '1px solid #bbf7d0', borderRadius: 999,
                           color: '#15803d', fontWeight: 700 }}>LATAM</span>
          )}
          <div style={{ width: 10, height: 10, borderRadius: '50%',
                        background: colors.dot,
                        boxShadow: colors.glow !== 'none'
                          ? `0 0 8px ${colors.dot}` : 'none' }} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
        {[
          { label: 'Queue', row: qd },
          { label: 'Site',  row: sr },
          { label: 'Proc',  row: pr },
        ].map(({ label, row }) => {
          const c = scoreToColor(row?.score ?? null);
          return (
            <div key={label} style={{
              background: c.bg, border: `1px solid ${c.border}`,
              borderRadius: 8, padding: '5px 4px', textAlign: 'center',
            }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: c.text }}>
                {row ? Math.round(row.score) : '\u2014'}
              </div>
              <div style={{ fontSize: 8, color: '#64748b',
                            textTransform: 'uppercase', fontWeight: 600 }}>
                {label}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 10, fontSize: 9, color: colors.text,
                    fontWeight: 600, textAlign: 'right' }}>
        View project flow \u2192
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// PHASE DRILLDOWN PANEL
// ─────────────────────────────────────────────────────────────

const PhasePanel = ({ phase, score, siteCorr004, siteName, onClose }) => {
  if (!phase) return null;
  const colors = scoreToColor(score);

  return (
    <div style={{
      position: 'fixed', right: 0, top: 0, bottom: 0, width: 380,
      background: '#fff', borderLeft: '1px solid #e2e8f0',
      boxShadow: '-8px 0 32px rgba(0,0,0,0.12)',
      zIndex: 1000, overflowY: 'auto', padding: 24,
      display: 'flex', flexDirection: 'column', gap: 16,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: 1 }}>
            {phase.id} \u00B7 {siteName}
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', marginTop: 4 }}>
            {phase.icon} {phase.label}
          </div>
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
            {phase.sub}
          </div>
        </div>
        <button
          onClick={onClose}
          style={{ background: '#f1f5f9', border: 'none', borderRadius: 8,
                   padding: '6px 10px', cursor: 'pointer', fontSize: 16 }}>
          \u2715
        </button>
      </div>

      <div style={{
        background: colors.bg, border: `2px solid ${colors.border}`,
        borderRadius: 12, padding: 16, textAlign: 'center',
      }}>
        <div style={{ fontSize: 36, fontWeight: 900, color: colors.text }}>
          {score !== null ? `${Math.round(score)}/100` : '\u2014'}
        </div>
        <div style={{ fontSize: 12, color: colors.text, fontWeight: 700,
                      marginTop: 4 }}>
          {colors.label}
        </div>
      </div>

      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#374151',
                      textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
          Activities
        </div>
        {phase.activities.map((a, i) => (
          <div key={i} style={{
            padding: '10px 12px', background: '#f8fafc',
            border: '1px solid #e2e8f0', borderRadius: 8,
            marginBottom: 6, fontSize: 12, color: '#374151',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%',
                          background: colors.border, flexShrink: 0 }} />
            {a}
          </div>
        ))}
      </div>

      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#374151',
                      textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
          CORR-004 Evidence
        </div>
        {phase.corr004Keys.length === 0 ? (
          <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>
            No public dataset correlators for this phase yet.
            AISentry commissioning data coming in Phase 2.
          </div>
        ) : phase.corr004Keys.map(key => {
          const row = siteCorr004?.[key];
          const c   = scoreToColor(row?.score ?? null);
          return (
            <div key={key} style={{
              background: c.bg, border: `1px solid ${c.border}`,
              borderRadius: 10, padding: 12, marginBottom: 8,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between',
                            alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: '#374151',
                               textTransform: 'uppercase', letterSpacing: 1 }}>
                  {key.replace('_', ' ')}
                </span>
                <span style={{ fontSize: 13, fontWeight: 800, color: c.text }}>
                  {row ? `${Math.round(row.score)}/100` : '\u2014'}
                </span>
              </div>
              {row?.summary && (
                <div style={{ fontSize: 10, color: '#475569', lineHeight: 1.5 }}>
                  {row.summary}
                </div>
              )}
              {row?.recommendations && Array.isArray(row.recommendations) && (
                <div style={{ marginTop: 8 }}>
                  {row.recommendations.slice(0, 2).map((r, i) => (
                    <div key={i} style={{ fontSize: 9, color: c.text,
                                          fontWeight: 600, marginTop: 4,
                                          display: 'flex', gap: 4 }}>
                      <span>\u2192</span><span>{r}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{
        background: 'linear-gradient(135deg, #f0f9ff, #e0f2fe)',
        border: '1px solid #bae6fd', borderRadius: 10, padding: 14,
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#0369a1',
                      marginBottom: 4 }}>
          AI Activity Breakdown
        </div>
        <div style={{ fontSize: 10, color: '#0284c7' }}>
          Decompose this phase into sub-activities with AI-generated risk analysis.
          Coming in Phase 2.
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────

const ProjectOracle = () => {
  const navigate = useNavigate();

  const [corr004,        setCorr004]        = useState({});
  const [loading,        setLoading]        = useState(true);
  const [selectedSite,   setSelectedSite]   = useState(null);
  const [selectedPhase,  setSelectedPhase]  = useState(null);
  const [phaseScore,     setPhaseScore]     = useState(null);
  const [view,           setView]           = useState('portfolio');
  const [drillPhaseId,   setDrillPhaseId]   = useState(null);
  const [showSiteSelector, setShowSiteSelector] = useState(false);

  const [flowNodes, setFlowNodes, onNodesChange] = useNodesState([]);
  const [flowEdges, setFlowEdges, onEdgesChange] = useEdgesState([]);

  const fetchCorr004 = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/ingest/corr004/results`);
      if (!res.ok) return;
      const data = await res.json();
      const indexed = {};
      for (const row of data.results || []) {
        if (!indexed[row.edge_node_id]) indexed[row.edge_node_id] = {};
        indexed[row.edge_node_id][row.correlator] = row;
      }
      setCorr004(indexed);
    } catch (e) {
      console.warn('[ProjectOracle] CORR-004 fetch failed:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCorr004(); }, [fetchCorr004]);

  // Deep-link from DC Fleet: if localStorage has a site code, open that site directly
  useEffect(() => {
    try {
      const code = localStorage.getItem('projectOracleSelectedSite');
      if (code) {
        localStorage.removeItem('projectOracleSelectedSite');
        const match = SITES.find(s => s.code === code);
        if (match) {
          setSelectedSite(match);
          setSelectedPhase(null);
          setView('site');
        }
      }
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (view !== 'site' || !selectedSite) return;
    const siteCorr004 = corr004[selectedSite.code] || null;
    const { nodes, edges } = buildSiteFlow(
      selectedSite,
      siteCorr004,
      (phase) => { setDrillPhaseId(phase.id); setView('phase'); }
    );
    console.log('[Oracle] nodes:', nodes.length, 'edges:', edges.length);
    setFlowNodes(nodes);
    setFlowEdges(edges);
  }, [view, selectedSite, corr004, setFlowNodes, setFlowEdges]);

  useEffect(() => {
    if (!showSiteSelector) return;
    const handler = (e) => {
      if (!e.target.closest('[data-site-selector]'))
        setShowSiteSelector(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSiteSelector]);

  const handleSiteClick = (site) => {
    if (!site) {
      setView('portfolio');
      setSelectedSite(null);
      setSelectedPhase(null);
      return;
    }
    setSelectedSite(site);
    setSelectedPhase(null);
    setView('site');
  };

  const handleBackToPortfolio = () => {
    setView('portfolio');
    setSelectedSite(null);
    setSelectedPhase(null);
  };

  const portfolioStats = useMemo(() => {
    const usSites    = SITES.filter(s => s.region === 'US');
    const highRisk   = usSites.filter(s => {
      const score = getSiteOverallScore(corr004[s.code]);
      return score !== null && score >= 75;
    });
    return {
      total:     SITES.length,
      us:        usSites.length,
      latam:     SITES.filter(s => s.region === 'LATAM').length,
      highRisk:  highRisk.length,
      monitored: Object.keys(corr004).length,
    };
  }, [corr004]);

  // Layer 3: Phase Activity Drilldown
  if (view === 'phase') {
    return (
      <PhaseOracle
        site={selectedSite}
        initialPhaseId={drillPhaseId}
        siteCorr004={corr004[selectedSite?.code] || null}
        onBack={(dest) => {
          if (dest === 'site') {
            setView('site');
          } else {
            setView('portfolio');
            setSelectedSite(null);
          }
          setDrillPhaseId(null);
        }}
      />
    );
  }

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
                  height: 'calc(100vh - 64px)', background: '#fff' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 48, height: 48, border: '4px solid #e0f2fe',
                      borderTopColor: '#0ea5e9', borderRadius: '50%',
                      animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
        <div style={{ color: '#0ea5e9', fontWeight: 600 }}>Loading Project Flow...</div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );

  return (
    <div style={{ height: 'calc(100vh - 64px)', background: '#f8fafc',
                  position: 'relative', overflow: 'hidden' }}>

      {/* Header */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0',
                    padding: '16px 32px',
                    position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10,
                    display: 'flex',
                    alignItems: 'center', justifyContent: 'space-between',
                    flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {view === 'site' && (
            <button
              onClick={handleBackToPortfolio}
              style={{ background: '#f1f5f9', border: '1px solid #e2e8f0',
                       borderRadius: 8, padding: '6px 12px', cursor: 'pointer',
                       fontSize: 12, color: '#374151', fontWeight: 600,
                       display: 'flex', alignItems: 'center', gap: 6 }}>
              \u2190 Portfolio
            </button>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40,
                          background: 'linear-gradient(135deg, #1e3a8a, #1e40af)',
                          borderRadius: 12, display: 'flex', alignItems: 'center',
                          justifyContent: 'center' }}>
              <svg width="20" height="20" fill="none" stroke="white"
                   strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round"
                      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
              </svg>
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <h1 style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', margin: 0 }}>
                  Project Flow
                </h1>
                <span style={{ fontSize: 9, padding: '2px 8px',
                               background: '#f0f9ff', border: '1px solid #bae6fd',
                               borderRadius: 999, color: '#0369a1', fontWeight: 700 }}>
                  AXIOM INTELLIGENCE
                </span>
              </div>
              <p style={{ fontSize: 12, color: '#64748b', margin: '2px 0 0' }}>
                {view === 'portfolio'
                  ? `Owner's project intelligence \u00B7 ${portfolioStats.total} sites \u00B7 Aligned Data Centers`
                  : `${selectedSite?.name} \u00B7 ${selectedSite?.city} \u00B7 Project Flow`}
              </p>
            </div>
          </div>
          <SiteSelector
            sites={SITES}
            selectedSite={selectedSite}
            onSelect={handleSiteClick}
            show={showSiteSelector}
            onToggle={() => setShowSiteSelector(p => !p)}
          />
        </div>

      </div>

      {/* PORTFOLIO VIEW */}
      {view === 'portfolio' && (
        <div style={{ position: 'absolute', top: 52, left: 0,
                      right: 0, bottom: 0, overflowY: 'auto',
                      background: '#020617' }}>
          <PortfolioCommandCenter
            sites={SITES}
            corr004={corr004}
            onSiteClick={handleSiteClick}
          />
        </div>
      )}

      {/* SITE FLOW VIEW */}
      {view === 'site' && (
        <div style={{ position: 'absolute', top: 80, left: 0,
                      right: 0, bottom: 0 }}>
          <ReactFlow
            nodes={flowNodes}
            edges={flowEdges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            minZoom={0.3}
            maxZoom={1.5}
            proOptions={{ hideAttribution: true }}
          >
            <Background variant="dots" gap={20} size={1}
                        color="rgba(0,0,0,0.04)" />
            <Controls className="!bg-white !border-slate-200 !rounded-xl !shadow-lg" />
          </ReactFlow>

          <PhasePanel
            phase={selectedPhase}
            score={phaseScore}
            siteCorr004={corr004[selectedSite?.code] || null}
            siteName={selectedSite?.name}
            onClose={() => setSelectedPhase(null)}
          />
        </div>
      )}
    </div>
  );
};

export default ProjectOracle;
